import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const _cache = new Map();
const CACHE_TTL = 90 * 60 * 1000; // 90 min (server signs for 2h, refresh early)

const isExpired = (entry) => Date.now() - entry.ts > CACHE_TTL;

const cacheKey = (chapterId, keys) => {
  const suffix = keys ? [...keys].sort().join(',') : '_all';
  return `${chapterId}:${suffix}`;
};

/**
 * Fetches time-limited signed OSS URLs for a chapter's audio/srt files.
 * Returns { urls, loading, error, refresh }.
 *
 * @param {number|null} chapterId
 * @param {string[]|null} keys  - optional subset of keys to fetch, e.g. ['en_mp3','en_srt'].
 *                                 If null/undefined, fetches all.
 *
 * `urls` shape (all values are full signed URLs or ''):
 *   en_mp3, cn_mp3, en_cn_mp3, cn_en_mp3,
 *   en_srt, cn_srt, en_cn_srt, cn_en_srt,
 *   podcast_mp3, podcast_srt
 */
const useAudioUrls = (chapterId, keys = null) => {
  const [urls, setUrls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const keysStr = keys ? [...keys].sort().join(',') : null;

  const fetchUrls = useCallback(
    async (force = false) => {
      if (!chapterId) {
        setUrls(null);
        return;
      }
      const ck = cacheKey(chapterId, keys);
      const cached = _cache.get(ck);
      if (!force && cached && !isExpired(cached)) {
        setUrls(cached.data);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = keysStr ? { keys: keysStr } : {};
        const { data } = await api.get(`/chapters/${chapterId}/audio-urls`, { params });
        _cache.set(ck, { data, ts: Date.now() });
        setUrls(data);
      } catch (err) {
        console.error('Failed to fetch audio URLs:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [chapterId, keysStr, keys],
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!chapterId) {
        setUrls(null);
        return;
      }
      const ck = cacheKey(chapterId, keys);
      const cached = _cache.get(ck);
      if (cached && !isExpired(cached)) {
        setUrls(cached.data);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = keysStr ? { keys: keysStr } : {};
        const { data } = await api.get(`/chapters/${chapterId}/audio-urls`, { params });
        if (cancelled) return;
        _cache.set(ck, { data, ts: Date.now() });
        setUrls(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch audio URLs:', err);
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [chapterId, keysStr, keys]);

  return { urls, loading, error, refresh: fetchUrls };
};

/**
 * Imperative helper (non-hook) — fetches signed URLs for a chapter.
 * Uses the same module-level cache so results are shared with the hook.
 */
export const fetchAudioUrls = async (chapterId, keys = null) => {
  if (!chapterId) return null;
  const ck = cacheKey(chapterId, keys);
  const cached = _cache.get(ck);
  if (cached && !isExpired(cached)) return cached.data;
  try {
    const keysStr = keys ? [...keys].sort().join(',') : null;
    const params = keysStr ? { keys: keysStr } : {};
    const { data } = await api.get(`/chapters/${chapterId}/audio-urls`, { params });
    _cache.set(ck, { data, ts: Date.now() });
    return data;
  } catch (err) {
    console.error('Failed to fetch audio URLs:', err);
    return null;
  }
};

export default useAudioUrls;
